import React, { useEffect, useState } from 'react';
import { Button, Snackbar, Avatar } from '@mui/material';
import styles from './trending-messeges.module.scss';
import { AlertService } from '../../../../common/services/alert/alert.service';
import {
  IUserData,
  IUserBadgesData,
  ISubscriptionData,
  IConnectionData,
} from '../../../../common/services/alert/alert.interface';
import { QCNewLogo } from '../../../../assets/svg';
import { countryCodes } from '../../../contact-us/container/constants.js';
import { getSubscriptionType } from '../../../utils/utils.common';

const alertServices = new AlertService();

type AlertType = 'User' | 'Badges' | 'Subscription' | 'Connection';
type AlertData =
  | IUserData
  | IUserBadgesData
  | ISubscriptionData
  | IConnectionData;
interface AlertState {
  data: AlertData | null;
  show: boolean;
}
interface AlertsState {
  user: AlertState;
  badges: AlertState;
  subscription: AlertState;
  connection: AlertState;
}

const TrendingMesseges: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertsState>({
    user: { data: null, show: false },
    badges: { data: null, show: false },
    subscription: { data: null, show: false },
    connection: { data: null, show: false },
  });
  const [isPrevDaysData, setIsPrevDaysData] = useState(false);

  const fetchAlertData = async (
    type: AlertType,
    duration: string,
    prevDuration?: string,
  ) => {
    try {
      const method = `get${type}Alert` as keyof AlertService;
      const response = await alertServices[method](duration);
      setAlerts(prev => ({
        ...prev,
        [type.toLowerCase()]: { data: response, show: response !== null },
      }));
      if (response === null && prevDuration) {
        setIsPrevDaysData(true);
        await alertServices[method](prevDuration);
      } else {
        setIsPrevDaysData(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const alertTypes: AlertType[] = [
      'User',
      'Badges',
      'Subscription',
      'Connection',
    ];
    const intervals: Record<string, NodeJS.Timeout> = {};

    const initializeAlerts = () => {
      alertTypes.forEach(type => {
        fetchAlertData(type, '1m', '3d');
        intervals[type] = setInterval(
          () => fetchAlertData(type, '1m'),
          60000,
          undefined,
        );
      });
    };

    const timeoutId = setTimeout(initializeAlerts, 3000);

    return () => {
      clearTimeout(timeoutId);
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, []);

  const handleCloseAlert = (type: string) => {
    setAlerts(prev => ({
      ...prev,
      [type]: { ...prev[type as keyof AlertsState], show: false },
    }));
  };

  const renderMessage = (data: AlertData | null, type: string) => {
    if (!data) return null;

    const { displayName, cityName, countryCode, profileImageURL } = data;
    const countryName = countryCodes.find(
      country => country.code === countryCode?.toLowerCase(),
    )?.name;

    if (countryName === undefined) {
      console.error(`Country name not found for code: ${countryCode}`);
      return null;
    }

    switch (type) {
      case 'user':
        if ('platform' in data) {
          return (
            <div className={styles.wrapper}>
              <div className={styles.profile}>
                <Avatar src={profileImageURL || QCNewLogo} />
              </div>
              {displayName} from {`${cityName}, ${countryName}`}{' '}
              {isPrevDaysData ? 'recently' : 'just'} signed up on{' '}
              {data.platform}
              <Button
                color="secondary"
                size="small"
                className={styles.btnTrending}
              >
                Trending
              </Button>
            </div>
          );
        }
        return null;

      case 'badges':
        if ('badgeName' in data && 'platform' in data) {
          return (
            <div className={styles.wrapper}>
              <div className={styles.profile}>
                <Avatar src={profileImageURL || QCNewLogo} />
              </div>
              {displayName} from {`${cityName}, ${countryName}`} just earned{' '}
              {data.badgeName} on {data.platform}
              <Button
                color="secondary"
                size="small"
                className={styles.btnTrending}
              >
                Trending
              </Button>
            </div>
          );
        }
        return null;

      case 'subscription':
        if ('subscriptionType' in data && 'platform' in data) {
          return (
            <div className={styles.wrapper}>
              <div className={styles.profile}>
                <Avatar src={profileImageURL || QCNewLogo} />
              </div>
              {displayName} from {`${cityName}, ${countryName}`} just subscribed
              to {getSubscriptionType(data.subscriptionType || '')} on{' '}
              {data.platform}
              <Button
                color="secondary"
                size="small"
                className={styles.btnTrending}
              >
                Trending
              </Button>
            </div>
          );
        }
        return null;

      case 'connection':
        if ('referralRank' in data) {
          return (
            <div className={styles.wrapper}>
              <div className={styles.profile}>
                <Avatar src={profileImageURL || QCNewLogo} />
              </div>
              {displayName} from {`${cityName}, ${countryName}`} just referred
              their {data.referralRank} referral who subscribed!
              <Button
                color="secondary"
                size="small"
                className={styles.btnTrending}
              >
                Trending
              </Button>
            </div>
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <>
      {Object.entries(alerts).map(
        ([type, { data, show }]) =>
          data &&
          !data.displayName?.includes('test') && (
            <Snackbar
              key={type}
              open={show}
              onClose={() => handleCloseAlert(type)}
              message={renderMessage(data, type)}
              autoHideDuration={30000}
              ContentProps={{
                classes: {
                  root: styles.container,
                  action: styles.action,
                },
              }}
            />
          ),
      )}
    </>
  );
};

export default TrendingMesseges;
